import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
});

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  (typeof error?.response?.data === 'string' ? error.response.data : '') ||
  error?.message ||
  fallback;

const persistAuth = (payload) => {
  if (payload?.token) {
    localStorage.setItem('token', payload.token);
  }
  if (payload?.user) {
    localStorage.setItem('user', JSON.stringify(payload.user));
  }
  window.dispatchEvent(new Event('auth-changed'));
};

export const register = async ({
  firstName,
  lastName,
  email,
  password,
  phone,
  consentTerms,
  consentMarketing,
}) => {
  try {
    const response = await api.post('/auth/register', {
      firstName,
      lastName,
      email,
      password,
      phone,
      consentTerms,
      consentMarketing,
    });
    return response.data;
  } catch (error) {
    throw getErrorMessage(error, 'Registration failed');
  }
};

export const loginWithGoogle = async ({ idToken, consentTerms = true, consentMarketing = false }) => {
  try {
    const response = await api.post('/auth/oauth/google', {
      idToken,
      consentTerms,
      consentMarketing,
    });
    persistAuth(response.data);
    return response.data;
  } catch (error) {
    throw getErrorMessage(error, 'Google login failed');
  }
};

export const login = async (credentials) => {
  try {
    const response = await api.post('/auth/login', credentials);
    if (!response.data?.mfa_required) {
      persistAuth(response.data);
    }
    return response.data;
  } catch (error) {
    throw getErrorMessage(error, 'Login failed');
  }
};

export const verifyAdminMfa = async ({ token, code }) => {
  try {
    const response = await api.post('/auth/mfa/verify', { token, code });
    persistAuth(response.data);
    return response.data;
  } catch (error) {
    throw getErrorMessage(error, 'MFA verification failed');
  }
};

export const requestPasswordReset = async (email) => {
  try {
    const response = await api.post('/auth/password/reset-request', { email });
    return response.data;
  } catch (error) {
    throw getErrorMessage(error, 'Password reset request failed');
  }
};

export const confirmPasswordReset = async ({ token, newPassword }) => {
  try {
    const response = await api.post('/auth/password/reset-confirm', { token, newPassword });
    return response.data;
  } catch (error) {
    throw getErrorMessage(error, 'Password reset failed');
  }
};

export const syncCurrentUser = async () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  try {
    const response = await api.get('/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.data?.authenticated && response.data?.user) {
      localStorage.setItem('user', JSON.stringify(response.data.user));
      return response.data.user;
    }
    logout();
    return null;
  } catch (error) {
    logout();
    return null;
  }
};

export const getCurrentUser = () => {
  try {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  } catch (error) {
    return null;
  }
};

export const logout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('cart');
  window.dispatchEvent(new Event('auth-changed'));
};

export const isAuthenticated = () => Boolean(localStorage.getItem('token'));
